import { useEffect, useRef } from "react";
import { useCallerState } from "./callerState";
import { STATES } from "./states";

const known = (s: string | undefined) => (s && STATES.some((x) => x.code === s) ? s : undefined);

/**
 * One caller's state for the whole call: the top bar's. A page whose link carries a state (`?state=XX`, from the
 * problem list, search or a shared link) makes it the caller's state on arrival; after that the top bar (or a
 * select on the page that sets the caller's state) drives the page, and the link follows it, replaced rather than
 * pushed, so the page, the top bar and the program panel always agree. Returns the state the page shows.
 */
export function useLinkedCallerState(linkState: string | undefined, setLinkState: (s: string | undefined) => void): string | undefined {
  const caller = useCallerState();
  const fromLink = known(linkState);
  const seenLink = useRef<string | undefined>(undefined);
  const seenCaller = useRef(caller.state);
  // A link state not yet adopted wins for this render, so the page does not load the old state first.
  const arriving = fromLink !== undefined && fromLink !== seenLink.current && fromLink !== caller.state;

  useEffect(() => {
    if (fromLink === seenLink.current) return;
    seenLink.current = fromLink;
    if (fromLink !== undefined && fromLink !== caller.state) caller.setState(fromLink);
    // Only a new link state is adopted; a top-bar change must not be undone by the link it is about to replace.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromLink]);

  useEffect(() => {
    if (caller.state === seenCaller.current) return;
    seenCaller.current = caller.state;
    if (caller.state !== linkState) setLinkState(caller.state);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caller.state]);

  return arriving ? fromLink : caller.state;
}
